import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LayoutGrid, FileInput, Workflow, ArrowRight, MessageSquareText, Check, Sparkles, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BlueprintBackground } from "@/components/BlueprintBackground";
import { Logo } from "@/components/Logo";
import { Wordmark } from "@/components/Wordmark";
import { useAuth } from "@/contexts/AuthContext";
import junkshonIcon from "@/assets/junkshon-icon.webp";

const FEATURES = [
  {
    icon: Workflow,
    title: "Standard ArchiMate Modeling",
    desc: "Full ArchiMate 3.x notation across Strategy, Business, Application, Technology and Motivation layers.",
  },
  {
    icon: FileInput,
    title: "Import in Seconds",
    desc: "Bring in existing .archimate files or paste a text description and let ModernTect draft your model for review.",
  },
  {
    icon: MessageSquareText,
    title: "Architect Assistant",
    desc: "Ask questions or request changes in plain English - the copilot proposes edits you approve or reject.",
  },
  {
    icon: LayoutGrid,
    title: "Interactive Canvas",
    desc: "Drag-and-drop diagramming with correct ArchiMate shapes, connectors, auto-layout and grouping.",
  },
  {
    icon: FileText,
    title: "Reports & Documentation",
    desc: "Generate a Standard Report, Gap Analysis or an AI-written High-Level Design (HLD) as polished PDF and Word documents, on demand.",
  },
];

export default function Landing() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("annual");

  return (
    <div className="min-h-screen text-white" data-testid="landing-page">
      <BlueprintBackground />

      <header className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Logo size={24} />
          <Wordmark className="font-heading text-lg sm:text-xl font-bold tracking-tight" />
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {isAuthenticated ? (
            <Button
              data-testid="landing-nav-dashboard-button"
              onClick={() => navigate("/dashboard")}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700"
            >
              Go to Dashboard
            </Button>
          ) : (
            <>
              <Link to="/login" data-testid="landing-nav-login-link">
                <Button variant="ghost" size="sm" className="text-slate-200 hover:text-white hover:bg-slate-800">
                  Log In
                </Button>
              </Link>
              <Link to="/signup" data-testid="landing-nav-signup-link">
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700">Get Started</Button>
              </Link>
            </>
          )}
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-16 sm:pb-24 grid md:grid-cols-2 gap-10 sm:gap-12 items-center">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-6">
            Enterprise architecture,
            <br />
            <span className="text-blue-400">modelled with you.</span>
          </h1>
          <p className="text-slate-300 text-base sm:text-lg mb-8 max-w-md">
            A modern ArchiMate tool that imports your existing models, drafts new ones from plain text,
            and lets you chat with using the Architect assistant to build and refine your architecture.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Button
              size="lg"
              data-testid="landing-hero-get-started-button"
              onClick={() => navigate(isAuthenticated ? "/dashboard" : "/signup")}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6"
            >
              {isAuthenticated ? "Go to Dashboard" : "Start Modeling Free"} <ArrowRight size={16} className="ml-1.5" />
            </Button>
            {!isAuthenticated && (
              <Link to="/login" data-testid="landing-hero-login-link" className="text-slate-300 hover:text-white text-sm font-medium">
                I already have an account
              </Link>
            )}
          </div>
        </div>
        <div className="relative rounded-2xl overflow-hidden border border-slate-700/60 shadow-2xl">
          <img
            src="https://images.unsplash.com/photo-1600132806370-bf17e65e942f?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Architectural blueprint wireframe"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-transparent to-transparent" />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
        <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight text-slate-100 mb-8">
          How Architecture should be developed
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              data-testid={`landing-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-xl border border-slate-700/60 bg-slate-900/60 backdrop-blur-sm p-5 hover:border-blue-500/50 transition-colors"
            >
              <div className="h-9 w-9 rounded-lg bg-blue-500/15 flex items-center justify-center mb-4">
                <f.icon size={17} className="text-blue-400" />
              </div>
              <h3 className="text-base font-semibold text-white mb-1.5">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24 text-center" data-testid="landing-pricing-section">
        <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight text-slate-100 mb-2">Simple pricing</h2>
        <p className="text-slate-400 text-sm mb-6">Start free. Upgrade when you outgrow one project.</p>

        <div
          className="inline-flex items-center gap-1 bg-slate-800/60 border border-slate-700 rounded-full p-1 mb-8"
          data-testid="landing-pricing-toggle"
        >
          <button
            type="button"
            onClick={() => setBillingCycle("monthly")}
            data-testid="landing-pricing-toggle-monthly"
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              billingCycle === "monthly" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingCycle("annual")}
            data-testid="landing-pricing-toggle-annual"
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              billingCycle === "annual" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"
            }`}
          >
            Annual
            <span className="text-[10px] font-semibold bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full">
              Save £10.80/yr
            </span>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto text-left">
          <div
            data-testid="landing-pricing-free-card"
            className="rounded-xl border border-slate-700/60 bg-slate-900/60 backdrop-blur-sm p-6 sm:p-8 flex flex-col"
          >
            <h3 className="text-base font-semibold text-white mb-1">Free</h3>
            <div className="flex items-baseline gap-1.5 mb-1">
              <span className="font-heading text-3xl sm:text-4xl font-extrabold text-white">£0</span>
            </div>
            <p className="text-sm text-slate-400 mb-6">1 project, forever.</p>
            <ul className="space-y-2.5 text-sm text-slate-300 mb-8 flex-1">
              <li className="flex items-start gap-2">
                <Check size={15} className="text-blue-400 mt-0.5 shrink-0" /> Core ArchiMate modeling
              </li>
              <li className="flex items-start gap-2">
                <Check size={15} className="text-blue-400 mt-0.5 shrink-0" /> Architect AI assistant
              </li>
              <li className="flex items-start gap-2">
                <Check size={15} className="text-blue-400 mt-0.5 shrink-0" /> One active project
              </li>
            </ul>
            <Link to="/signup" data-testid="landing-pricing-free-cta">
              <Button variant="outline" className="w-full border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white">
                Get Started
              </Button>
            </Link>
          </div>

          <div
            data-testid="landing-pricing-unlimited-card"
            className="relative rounded-xl border-2 border-blue-500 bg-slate-900/60 backdrop-blur-sm p-6 sm:p-8 flex flex-col shadow-[0_0_40px_-10px_rgba(59,130,246,0.4)]"
          >
            <span
              data-testid="landing-pricing-recommended-badge"
              className="absolute -top-3 left-6 flex items-center gap-1 bg-blue-600 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full"
            >
              <Sparkles size={11} /> Recommended
            </span>
            <h3 className="text-base font-semibold text-white mb-1">Unlimited</h3>
            {billingCycle === "monthly" ? (
              <>
                <div className="flex items-baseline gap-1.5 mb-1">
                  <span className="font-heading text-3xl sm:text-4xl font-extrabold text-white">£9.00</span>
                  <span className="text-sm font-normal text-slate-400">/mo incl. VAT</span>
                </div>
                <p className="text-sm text-slate-400 mb-1">£7.50/mo excl. VAT, per user</p>
                <p className="text-sm text-slate-400 mb-6">Switch to annual billing and save £10.80/year</p>
              </>
            ) : (
              <>
                <div className="flex items-baseline gap-1.5 mb-1">
                  <span className="font-heading text-3xl sm:text-4xl font-extrabold text-white">£8.10</span>
                  <span className="text-sm font-normal text-slate-400">/mo incl. VAT</span>
                </div>
                <p className="text-sm text-slate-400 mb-1">£6.75/mo excl. VAT, per user</p>
                <p className="text-sm text-slate-400 mb-1">Billed as £97.20/year incl. VAT upfront</p>
                <span data-testid="landing-pricing-savings-badge" className="inline-block text-xs font-medium text-blue-400 mb-6">
                  Save £10.80/year with annual billing
                </span>
              </>
            )}
            <ul className="space-y-2.5 text-sm text-slate-300 mb-8 flex-1">
              <li className="flex items-start gap-2">
                <Check size={15} className="text-blue-400 mt-0.5 shrink-0" /> Everything in Free
              </li>
              <li className="flex items-start gap-2">
                <Check size={15} className="text-blue-400 mt-0.5 shrink-0" /> Unlimited projects and views
              </li>
              <li className="flex items-start gap-2">
                <Check size={15} className="text-blue-400 mt-0.5 shrink-0" /> No caps, ever
              </li>
            </ul>
            <Link to="/signup" data-testid="landing-pricing-unlimited-cta">
              <Button className="w-full bg-blue-600 hover:bg-blue-700">Get Started</Button>
            </Link>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-6" data-testid="landing-pricing-disclaimer">
          Prices are per user, in GBP, and include VAT where stated.
        </p>
      </section>

      <footer className="border-t border-slate-800 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center text-slate-500 text-sm space-y-4">
          <div>
            <Wordmark accentClassName="text-blue-500" /> &middot; Built for architects
          </div>
          <div className="flex items-center justify-center gap-2" data-testid="landing-footer-junkshon-brand">
            <img src={junkshonIcon} alt="" className="h-5 w-5 rounded-sm" />
            <span className="text-xs text-slate-500 tracking-wide">Junkshon</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
